import { useRef, useState } from "react";
import IconStarFill from "@/components/icons/icon-star-fill";

export interface TestimonialItem {
	id: string;
	name: string;
	role: string;
	company: string;
	avatarUrl?: string;
	rating: number;
	content: string;
	/** Content type: text, video, or audio */
	contentType?: string;
	/** Mux playback ID for video testimonials */
	muxPlaybackId?: string;
	/** Thumbnail URL for video testimonials */
	videoThumbnailUrl?: string;
	/** Audio URL for audio testimonials */
	audioUrl?: string;
	/** Pre-calculated audio duration from the API */
	audioDuration?: number | null;
	/** Consent type (e.g. "anonymous") */
	consent?: string | null;
}

export interface TestimonialListProps {
	/** Array of testimonials to display */
	testimonials: TestimonialItem[];
	/** Spacing between cards in pixels */
	cardSpacing?: number;
	/** Whether to show the star rating */
	showRating?: boolean;
	/** Whether to show the author avatar */
	showAvatar?: boolean;
	/** Accent color from brandkit */
	accentColor?: string;
	/** Custom color for rating stars */
	ratingStarColor?: string;
	/** Callback when a video testimonial play button is clicked */
	onVideoPlay?: (playbackId: string) => void;
	/** Optional className for additional styling */
	className?: string;
}

// Helper function to get initials
function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
}

// Helper function to get gradient based on name
function getGradient(name: string) {
	const gradients = [
		"from-violet-500 to-purple-600",
		"from-blue-500 to-cyan-500",
		"from-emerald-500 to-teal-500",
		"from-orange-500 to-amber-500",
		"from-pink-500 to-rose-500",
		"from-indigo-500 to-blue-500",
	];
	const index = name.charCodeAt(0) % gradients.length;
	return gradients[index];
}

function AudioPlayer({ src, initialDuration }: { src: string; initialDuration?: number | null }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time)) return "0:00";
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const togglePlay = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0 && !initialDuration) {
			setDuration(audioDur);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	return (
		<div className="mt-6 mb-4 p-3 rounded-xl bg-gray-100">
			<audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />
			<div className="flex items-center gap-3">
				<button type="button" onClick={togglePlay} className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 shadow-sm text-black bg-white">
					{isPlaying ? (
						<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>
				<div className="flex-1 flex items-center gap-2">
					<span className="text-xs tabular-nums text-gray-500">{formatTime(currentTime)}</span>
					<div className="flex-1 relative h-1.5 rounded-full overflow-hidden bg-gray-300/50">
						<div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: "#1f1f1f" }} />
						<input type="range" min={0} max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
					</div>
					{duration >= 1 && <span className="text-xs tabular-nums text-gray-500">{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}

// Individual testimonial card
function TestimonialCard({
	testimonial,
	showRating = true,
	showAvatar = true,
	ratingStarColor,
	onVideoPlay,
}: {
	testimonial: TestimonialItem;
	showRating?: boolean;
	showAvatar?: boolean;
	ratingStarColor?: string;
	onVideoPlay?: (playbackId: string) => void;
}) {
	const isVideo = testimonial.contentType === "video" && testimonial.muxPlaybackId;

	return (
		<div className="relative bg-white rounded-2xl border border-gray-100 overflow-hidden">
			<div className="p-5">
				{/* Rating */}
				{showRating && (
					<div className="flex items-center gap-0.5 mb-3">
						{[1, 2, 3, 4, 5].map((star) => (
							<IconStarFill
								key={star}
								className={`w-5 h-5 ${star <= testimonial.rating ? (ratingStarColor ? "" : "text-amber-400") : "text-gray-300"}`}
								style={star <= testimonial.rating && ratingStarColor ? { color: ratingStarColor } : undefined}
							/>
						))}
					</div>
				)}

				{/* Video thumbnail */}
				{isVideo && testimonial.videoThumbnailUrl && (
					<button
						type="button"
						onClick={() => testimonial.muxPlaybackId && onVideoPlay?.(testimonial.muxPlaybackId)}
						className="w-full overflow-hidden rounded-xl relative block cursor-pointer mb-4"
					>
						<img src={testimonial.videoThumbnailUrl} alt="" className="w-full h-40 object-cover" />
						<div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
							<div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: "#1f1f1f" }}>
								<svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M8 5v14l11-7z" />
								</svg>
							</div>
						</div>
					</button>
				)}

				{/* Audio player */}
				{testimonial.contentType === "audio" && testimonial.audioUrl && <AudioPlayer src={testimonial.audioUrl} initialDuration={testimonial.audioDuration} />}

				{/* Testimonial content */}
				<p className="text-gray-600 text-base leading-relaxed mb-4">{testimonial.content}</p>

				{/* Author info */}
				<div className="flex items-center gap-3 pt-3 border-t border-gray-100">
					{showAvatar &&
						(testimonial.avatarUrl ? (
							<img src={testimonial.avatarUrl} alt={testimonial.name} className="w-10 h-10 rounded-full object-cover" />
						) : (
							<div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getGradient(testimonial.name)} flex items-center justify-center text-white text-xs font-medium shadow-md`}>
								{getInitials(testimonial.name)}
							</div>
						))}
					<div className="flex-1 min-w-0">
						<p className="text-gray-900 font-medium text-sm truncate">{testimonial.name}</p>
						{testimonial.consent !== "anonymous" && (
							<p className="text-gray-500 text-xs truncate">
								{testimonial.role} at {testimonial.company}
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default function TestimonialList({ testimonials, cardSpacing = 16, showRating = true, showAvatar = true, ratingStarColor, onVideoPlay, className = "" }: TestimonialListProps) {
	return (
		<div className={`flex flex-col ${className}`} style={{ gap: `${cardSpacing}px` }}>
			{testimonials.map((testimonial) => (
				<TestimonialCard key={testimonial.id} testimonial={testimonial} showRating={showRating} showAvatar={showAvatar} ratingStarColor={ratingStarColor} onVideoPlay={onVideoPlay} />
			))}
		</div>
	);
}
